import { Button } from '@crehana/compass.ui/v3/button';

import { TimeAttendanceMarkType } from '@/generated/graphql/types';

import { useWidgetClockContext } from '../context-clock';
import { useTranslation } from '../i18n';
import type { TWidgetSize } from '../types';
import {
  ActionButtonMarkBreakEnd,
  ActionButtonMarkBreakStart,
  ActionButtonMarkEntry,
  ActionButtonMarkExit,
} from './action-buttons';
import { DateTimeDisplay } from './date-time-display';
import { MarkSlotsList } from './mark-slots-list';
import { TimerCircle } from './timer-circle';
import { WidgetError } from './widget-error';

type TWidgetContentProps = {
  size: TWidgetSize;
  timeZone: string;
  mode: 'widget' | 'card';
};

export const WidgetCard = ({ size, timeZone, mode }: TWidgetContentProps) => {
  const { t } = useTranslation();
  const { loading, error, data, refetch } = useWidgetClockContext();

  // In widget mode, cap size to 'm' so the timer uses 170px diameter
  // and buttons stay small — the widget container is always half-width.
  const effectiveSize: TWidgetSize =
    mode === 'widget' && size === 'l' ? 'm' : size;

  if (error) {
    return <WidgetError onRetry={() => refetch()} />;
  }

  const nextPendingMark = data?.next_pending_mark;

  // Renders buttons based on the current state:
  //
  // | next_pending_mark | Primary                        | Secondary (Salir)    |
  // |-------------------|--------------------------------|----------------------|
  // | Entry             | Marcar ingreso                 | Salir (disabled)     |
  // | BreakStart        | Iniciar almuerzo/descanso      | Salir (enabled)      |
  // | BreakEnd          | Finalizar almuerzo/descanso    | Salir (enabled)      |
  // | Exit              | Salir (promoted to primary)    | hidden               |
  //
  // When all marks are completed (exit marked), the WidgetClockProvider resets
  // the context to next_pending_mark=ENTRY, so it naturally falls into the
  // Entry case below — no special handling needed here.
  const renderActionButtons = () => {
    if (loading) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-1.2 tw-w-full">
          <Button disabled type="button" variant="primary">
            {t('action_buttons_mark_entry')}
          </Button>
          <Button disabled type="button" variant="secondary">
            {t('action_buttons_mark_exit')}
          </Button>
        </div>
      );
    }

    if (nextPendingMark === TimeAttendanceMarkType.Entry) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-0.8">
          <ActionButtonMarkEntry size={effectiveSize} />
          <ActionButtonMarkExit size={effectiveSize} disabled />
        </div>
      );
    }

    if (nextPendingMark === TimeAttendanceMarkType.BreakStart) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-0.8">
          <ActionButtonMarkBreakStart size={effectiveSize} />
          <ActionButtonMarkExit size={effectiveSize} />
        </div>
      );
    }

    if (nextPendingMark === TimeAttendanceMarkType.BreakEnd) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-0.8">
          <ActionButtonMarkBreakEnd size={effectiveSize} />
          <ActionButtonMarkExit size={effectiveSize} />
        </div>
      );
    }

    // Exit is the next action — promoted to primary, no secondary needed
    if (nextPendingMark === TimeAttendanceMarkType.Exit) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-0.8">
          <ActionButtonMarkExit size={effectiveSize} variant="primary" />
        </div>
      );
    }

    return null;
  };

  const timerAndButtonsLayout =
    mode === 'card'
      ? 'tw-flex tw-flex-col lg:tw-flex-row tw-gap-0.8 md:tw-gap-1.6 tw-items-center'
      : 'tw-flex tw-flex-col tw-gap-0.8 md:tw-gap-1.6 tw-items-center';

  const containerAlignItems =
    mode === 'card' ? 'tw-items-center' : 'tw-items-start';

  return (
    <div
      className={`tw-grid tw-grid-cols-12 ${containerAlignItems} tw-gap-0.8 sm:tw-gap-0 tw-w-full`}
    >
      <div className="tw-col-span-12 sm:tw-col-span-5 tw-space-y-1.6">
        <DateTimeDisplay loading={loading} timeZone={timeZone} />
        <div className="tw-hidden sm:tw-block">
          <MarkSlotsList timeZone={timeZone} />
        </div>
      </div>

      <div
        className={`tw-col-span-12 sm:tw-col-span-6 sm:tw-col-start-7 ${timerAndButtonsLayout}`}
      >
        <TimerCircle size={effectiveSize} />
        <div className="sm:tw-max-w-[30rem] tw-w-full tw-flex-1">
          {renderActionButtons()}
        </div>
      </div>
    </div>
  );
};
